import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useAppSelector } from '@redux/hooks';
import { useCallback, useMemo } from 'react';
import type { Decorated } from 'types/vendor';
import VendorAnalytics from '../../decorators/vendorAnalytics';

type Event =
	| 'Storefront Interaction'
	| 'Gallery Opened'
	| 'Gallery Closed'
	| 'Gallery Category Switched'
	| 'Gallery Album Selected'
	| 'Gallery Media Swiped';
export type StorefrontSection = 'gallery' | 'pricing' | 'reviews';
export type Source = 'main_storefront' | 'review_section' | 'pricing_section';
interface StorefrontGalleryPropertiesOnOpen {
	source?: string;
	mediaTypes?: string;
}
export type StorefrontGalleryPropertiesOnClose = {
	source?: string;
	mediaTypes?: string;
};
export type StorefrontGalleryPropertiesOnCategorySwitched = {
	sourceCategory?: string;
	destinationCategory?: string;
};
export type StorefrontGalleryPropertiesOnMediaSwiped = {
	categoryName?: string;
};

type StorefrontGalleryProperties =
	| StorefrontGalleryPropertiesOnOpen
	| StorefrontGalleryPropertiesOnClose
	| StorefrontGalleryPropertiesOnCategorySwitched
	| StorefrontGalleryPropertiesOnMediaSwiped;

type StorefrontGalleryEventTrackProperties = StorefrontGalleryProperties & {
	storefrontSection: StorefrontSection;
};

const getStorefrontGalleryOnOpenProperties = (
	storefrontSection: StorefrontSection,
	vendor: Decorated,
	properties?: StorefrontGalleryPropertiesOnOpen,
) => {
	const source = properties?.source || '';
	const mediaTypes = properties?.mediaTypes || '';
	return {
		storefrontSection,
		source,
		mediaTypes,
	};
};

const getStorefrontGalleryOnCloseProperties = (
	storefrontSection: StorefrontSection,
	vendor: Decorated,
	properties?: StorefrontGalleryPropertiesOnClose,
) => {
	const source = properties?.source || '';
	const mediaTypes = properties?.mediaTypes || '';
	return {
		storefrontSection,
		source,
		mediaTypes,
	};
};

const getStorefrontGalleryOnCategorySwitchedProperties = (
	storefrontSection: StorefrontSection,
	vendor: Decorated,
	properties?: StorefrontGalleryPropertiesOnCategorySwitched,
) => {
	const sourceCategory = properties?.sourceCategory || '';
	const destinationCategory = properties?.destinationCategory || '';

	return {
		storefrontSection,
		sourceCategory,
		destinationCategory,
	};
};

const getStorefrontGalleryOnMediaSwipedProperties = (
	storefrontSection: StorefrontSection,
	vendor: Decorated,
	properties?: StorefrontGalleryPropertiesOnMediaSwiped,
) => {
	const categoryName = properties?.categoryName || '';
	return {
		storefrontSection,
		categoryName,
	};
};

export const useTrackStorefrontGalleryInteraction = (
	storefrontSection: StorefrontSection,
	event: Event = 'Storefront Interaction',
	properties?: StorefrontGalleryProperties,
) => {
	const { track } = useAnalyticsContext();
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const vendorAnalytics = useMemo(() => new VendorAnalytics(vendor), [vendor]);
	const eventPropertiesMap = new Map<
		Event,
		(
			storefrontSection: StorefrontSection,
			vendor: Decorated,
			properties?: StorefrontGalleryProperties,
		) => StorefrontGalleryEventTrackProperties
	>([
		['Gallery Opened', getStorefrontGalleryOnOpenProperties],
		['Gallery Closed', getStorefrontGalleryOnCloseProperties],
		[
			'Gallery Category Switched',
			getStorefrontGalleryOnCategorySwitchedProperties,
		],
		['Gallery Media Swiped', getStorefrontGalleryOnMediaSwipedProperties],
	]);

	return useCallback(
		(mainProperties?: object) => {
			const eventPropertiesFn = eventPropertiesMap.get(event);
			track({
				event,
				properties: {
					...vendorAnalytics.baseEventProps(),
					...(eventPropertiesFn
						? eventPropertiesFn(storefrontSection, vendor, properties)
						: {}),
					...(mainProperties ?? {}),
				},
			});
		},
		[
			event,
			vendor,
			vendorAnalytics,
			track,
			storefrontSection,
			properties,
			eventPropertiesMap,
		],
	);
};
